import {
  ILeaderboardConfig,
  LeaderboardCompetitionType,
  LeaderboardFrequency,
  LeaderboardMetric,
} from 'fitbeat.models';
import { PouchDbQueryHelper } from './pouchdb-query-helper';

export class DbLeaderboardConfigRepository {
  private dbQueryHelper: PouchDbQueryHelper;

  constructor(private leaderboardConfigDb: any) {
    this.dbQueryHelper = new PouchDbQueryHelper(this.leaderboardConfigDb, 'DbLeaderboardConfigRepository');
  }

  public async add(leaderboardConfig: ILeaderboardConfig): Promise<void> {
    await this.leaderboardConfigDb.put(leaderboardConfig);
  }

  public async update(leaderboardConfig: ILeaderboardConfig): Promise<void> {
    await this.leaderboardConfigDb.put(leaderboardConfig);
  }

  public async get(leaderboardConfigId: string): Promise<ILeaderboardConfig> {
    const leaderboardConfigDb = await this.leaderboardConfigDb.get(leaderboardConfigId);
    return this.mapLeaderboardConfigDbObject(leaderboardConfigDb);
  }

  public async getApplicableLeaderboardConfigs(
    date: Date,
    organisation: string,
    usersGymName: string,
    dateFilterType: 'all' | 'active' | 'active-or-future',
  ): Promise<ILeaderboardConfig[]> {
    const isoStringDate = date.toISOString();
    const queryOptions = {
      selector: {},
    };

    if (dateFilterType === 'all') {
      queryOptions.selector = {
        organisation,
        $or: [
          {
            gymName: usersGymName,
          },
          {
            gymName: { $exists: false }, // the users gymname doesn't matter
          },
        ],
      };
    } else {
      const alreadyStarted = dateFilterType === 'active'
        ? { startDate: { $lt: isoStringDate } }
        : {};

      queryOptions.selector = {
        organisation,
        ...alreadyStarted,
        $or: [
          {
            gymName: usersGymName,
            frequency: { $ne: 'one-time' },
          },
          {
            gymName: usersGymName,
            frequency: 'one-time',
            endDate: { $gt: isoStringDate },
          },
          {
            gymName: { $exists: false },
            frequency: { $ne: 'one-time' },
          },
          {
            gymName: { $exists: false },
            frequency: 'one-time',
            endDate: { $gt: isoStringDate },
          },
        ],
      };
    }

    const result = await this.dbQueryHelper.queryAndGetDocs('getApplicableLeaderboardConfigs()', queryOptions);

    const orderedResult = result.sort((a, b) => a.order - b.order);
    return orderedResult.map(this.mapLeaderboardConfigDbObject);
  }

  public async getLeaderboardConfigsForOrganisation(
    organisation: string,
  ): Promise<ILeaderboardConfig[]> {
    const queryOptions = {
      selector: {
        organisation,
      },
    };

    const result = await this.dbQueryHelper.queryAndGetDocs('getLeaderboardConfigsForOrganisation()', queryOptions);
    return result.map(this.mapLeaderboardConfigDbObject);
  }

  public async getLeaderboardConfigsForGym(
    gymName: string,
  ): Promise<ILeaderboardConfig[]> {
    const queryOptions = {
      selector: {
        gymName,
      },
    };

    const result = await this.dbQueryHelper.queryAndGetDocs('getLeaderboardConfigsForGym()', queryOptions);
    return result.map(this.mapLeaderboardConfigDbObject);
  }

  private mapLeaderboardConfigDbObject(leaderboardConfigFromDB: any): ILeaderboardConfig {
    return {
      ...leaderboardConfigFromDB,
      startDate: new Date(leaderboardConfigFromDB.startDate),
      endDate: leaderboardConfigFromDB.endDate ? new Date(leaderboardConfigFromDB.endDate) : undefined,
      metric: leaderboardConfigFromDB.metric as LeaderboardMetric,
      frequency: leaderboardConfigFromDB.frequency as LeaderboardFrequency,
      competitionType: leaderboardConfigFromDB.competitionType as LeaderboardCompetitionType,
    };
  }
}
